import {
  BarChart3,
  Bell,
  Calendar,
  ChevronDown,
  CreditCard,
  LayoutDashboard,
  LogOut,
  Menu,
  Settings,
  Sparkles,
  Users,
  UsersRound,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';

import { useAuth, type Role } from '../../context/AuthContext';
import { useHeatherPanel } from '../../context/HeatherPanelContext';
import { HeatherPanel } from '../heather/HeatherPanel';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  roles?: Role[];
}

const NAV: NavItem[] = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/admin/events', label: 'Events', icon: Calendar },
  { to: '/admin/members', label: 'Members', icon: Users },
  { to: '/admin/payments', label: 'Payments', icon: CreditCard, roles: ['OWNER', 'ADMIN', 'TREASURER'] },
  { to: '/admin/notifications', label: 'Notifications', icon: Bell },
  { to: '/admin/users', label: 'Staff', icon: UsersRound, roles: ['OWNER', 'ADMIN'] },
  { to: '/admin/settings', label: 'Settings', icon: Settings, roles: ['OWNER', 'ADMIN'] },
  { to: '/admin/billing', label: 'Billing', icon: BarChart3, roles: ['OWNER', 'ADMIN'] },
];

export function AdminLayout() {
  const { user, logout, hasRole } = useAuth();
  const { setOpen: setHeatherOpen } = useHeatherPanel();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const items = NAV.filter((i) => !i.roles || hasRole(...i.roles));
  const canUseHeather = hasRole('OWNER', 'ADMIN');

  return (
    <div className="min-h-screen bg-cream-100">
      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between bg-navy-900 text-cream-100 px-4 h-14 shadow-card">
        <button
          onClick={() => setMobileOpen(true)}
          className="p-2 -ml-2 rounded hover:bg-navy-800"
          aria-label="Open navigation"
        >
          <Menu size={22} />
        </button>
        <Link to="/admin" className="font-display italic text-xl">
          {user?.org.name ?? 'MahjongHub'}
        </Link>
        <ChevronDown size={18} />
      </header>

      <div className="flex min-h-screen">
        <aside className="hidden lg:flex lg:flex-col lg:w-64 bg-navy-900 text-cream-100 shadow-card">
          <div className="px-5 py-5 border-b border-navy-700">
            <Link to="/admin" className="block">
              <div className="font-display italic text-2xl leading-tight">
                {user?.org.name ?? 'MahjongHub'}
              </div>
              <div className="text-xs text-cream-300 mt-0.5">Admin</div>
            </Link>
          </div>
          <nav className="flex-1 px-3 py-4 space-y-1">
            {items.map((i) => (
              <NavItemLink key={i.to} item={i} />
            ))}
          </nav>
          <AccountCard user={user} onLogout={logout} />
        </aside>

        {mobileOpen && (
          <div className="lg:hidden fixed inset-0 z-50" onClick={() => setMobileOpen(false)}>
            <div className="absolute inset-0 bg-navy-900/50" />
            <aside
              className="absolute left-0 top-0 h-full w-72 bg-navy-900 text-cream-100 shadow-cardHover flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-navy-700">
                <span className="font-display italic text-xl">{user?.org.name}</span>
                <button
                  onClick={() => setMobileOpen(false)}
                  className="p-1 rounded hover:bg-navy-800"
                  aria-label="Close navigation"
                >
                  <X size={20} />
                </button>
              </div>
              <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
                {items.map((i) => (
                  <NavItemLink key={i.to} item={i} onClick={() => setMobileOpen(false)} />
                ))}
              </nav>
              <AccountCard user={user} onLogout={logout} />
            </aside>
          </div>
        )}

        <main className="flex-1 min-w-0">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
            <Outlet key={location.pathname} />
          </div>
        </main>
      </div>

      {canUseHeather && (
        <>
          <button
            onClick={() => setHeatherOpen(true)}
            className="fixed bottom-6 right-6 z-30 flex items-center gap-2 px-4 py-2.5 rounded-full bg-rose-500 text-cream-100 shadow-cardHover hover:bg-rose-600 transition"
            aria-label="Open Heather (⌘K)"
            title="Open Heather (⌘K)"
          >
            <Sparkles size={16} />
            <span className="text-sm font-medium">Heather</span>
            <kbd className="hidden sm:inline text-[10px] font-mono bg-rose-600/60 px-1.5 py-0.5 rounded">
              ⌘K
            </kbd>
          </button>
          <HeatherPanel />
        </>
      )}
    </div>
  );
}

function NavItemLink({ item, onClick }: { item: NavItem; onClick?: () => void }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.to === '/admin'}
      onClick={onClick}
      className={({ isActive }) =>
        [
          'flex items-center gap-3 px-3 py-2 rounded-lg transition',
          isActive
            ? 'bg-rose-500 text-cream-100 shadow-card'
            : 'text-cream-200 hover:bg-navy-800',
        ].join(' ')
      }
    >
      <Icon size={18} />
      <span className="text-sm font-medium">{item.label}</span>
    </NavLink>
  );
}

function AccountCard({
  user,
  onLogout,
}: {
  user: ReturnType<typeof useAuth>['user'];
  onLogout: () => Promise<void>;
}) {
  if (!user) return null;
  return (
    <div className="border-t border-navy-700 p-4">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-rose-500 text-cream-100 text-sm font-semibold">
          {user.firstName.slice(0, 1)}
          {user.lastName.slice(0, 1)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">
            {user.firstName} {user.lastName}
          </div>
          <div className="text-xs text-cream-300 truncate">{user.email}</div>
        </div>
        <button
          onClick={onLogout}
          className="p-1.5 rounded hover:bg-navy-800 text-cream-200"
          aria-label="Log out"
        >
          <LogOut size={16} />
        </button>
      </div>
    </div>
  );
}
